// Thin wrapper around fetch for the MindlyFriend Flask API.
//
// Set REACT_APP_API_URL in .env to point at a deployed backend; defaults to the
// local Flask dev server.

/**
 * Resolves the API origin.
 *
 * Render's `fromService` blueprint reference yields a bare hostname
 * ("mindlyfriend-api.onrender.com") with no scheme, and CRA bakes env vars in as
 * literal strings at build time, so there is nowhere in the YAML to prepend
 * one. Normalising here means the blueprint can wire the two services together
 * without anyone editing a value by hand after the first deploy.
 *
 * A page served over HTTPS cannot call an HTTP origin — browsers block it as
 * mixed content — so anything schemeless is assumed to be HTTPS. Only
 * explicit localhost keeps http.
 */
function resolveBaseUrl() {
  const configured = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
  if (!configured) return 'http://localhost:5001';

  const withScheme = /^https?:\/\//i.test(configured)
    ? configured
    : /^localhost(:\d+)?$/i.test(configured)
      ? `http://${configured}`
      : `https://${configured}`;

  // A public host always contains a dot. A bare label here means the value came
  // from a platform reference that yields an *internal* service name — Render's
  // `fromService … property: host` does exactly this — which no browser can
  // resolve. That failure is otherwise silent: the app just falls back to mock
  // data and looks perfectly healthy.
  const host = withScheme.replace(/^https?:\/\//i, '').split('/')[0].split(':')[0];
  if (!host.includes('.') && host !== 'localhost') {
    // eslint-disable-next-line no-console
    console.error(
      `[MindlyFriend] REACT_APP_API_URL is "${configured}", which has no domain and ` +
        'cannot be reached from a browser. Set it to the full public URL ' +
        '(e.g. https://your-api.onrender.com) and rebuild — CRA inlines this at ' +
        'build time, so a restart alone will not pick it up.'
    );
  }

  return withScheme;
}

const BASE_URL = resolveBaseUrl();

// The bearer token for the signed-in customer. Held in a module variable rather
// than read from storage on every call, and set by AuthContext — that keeps the
// storage mechanism in one place and avoids a circular import between the
// client and the context.
let authToken = null;

export function setAuthToken(token) {
  authToken = token || null;
}

export function getAuthToken() {
  return authToken;
}

async function request(path, options = {}) {
  const { headers: overrideHeaders, ...rest } = options;

  const headers = {
    'Content-Type': 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    ...overrideHeaders,
  };

  const response = await fetch(`${BASE_URL}${path}`, { headers, ...rest });

  // Parse the body first — the API returns useful JSON on 4xx too (field-level
  // validation errors), and we don't want to throw that away.
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (!response.ok) {
    const error = new Error(
      (body && (body.message || body.error)) || `Request failed (${response.status})`
    );
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return body;
}

export const getStats = () => request('/api/stats');

export const getTherapists = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== '' && v != null)
  ).toString();
  return request(`/api/therapists${query ? `?${query}` : ''}`);
};

export const getCategories = () => request('/api/categories');

export const getFaqs = () => request('/api/faqs');

export const sendContactMessage = (payload) =>
  request('/api/contact', { method: 'POST', body: JSON.stringify(payload) });

// Hero search. The server records the request and emails it to the match inbox,
// so this is a submission rather than a query — there is no result set yet.
export const sendEnquiry = (payload) =>
  request('/api/enquiries', { method: 'POST', body: JSON.stringify(payload) });

// -- Auth -------------------------------------------------------------------

export const signup = (payload) =>
  request('/api/auth/signup', { method: 'POST', body: JSON.stringify(payload) });

export const login = (payload) =>
  request('/api/auth/login', { method: 'POST', body: JSON.stringify(payload) });

export const fetchCurrentUser = () => request('/api/auth/me');

export const requestPasswordReset = (email) =>
  request('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export const resetPassword = (payload) =>
  request('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// Public: anyone may apply. Creates no account and grants nothing.
export const submitApplication = (payload) =>
  request('/api/applications', { method: 'POST', body: JSON.stringify(payload) });

export const logout = () => request('/api/auth/logout', { method: 'POST' });

// -- Admin ------------------------------------------------------------------
// Every one of these requires an admin bearer token; the server returns 403 for
// a signed-in non-admin and 401 when the token is missing or expired.

const toQuery = (params = {}) =>
  new URLSearchParams(
    Object.entries(params).filter(
      ([, v]) => v !== '' && v != null && v !== 'all'
    )
  ).toString();

export const getAdminSummary = () => request('/api/admin/summary');

export const getAdminFilters = () => request('/api/admin/filters');

export const getAdminAppointments = (params = {}) => {
  const query = toQuery(params);
  return request(`/api/admin/appointments${query ? `?${query}` : ''}`);
};

export const updateAppointmentStatus = (id, status) =>
  request(`/api/admin/appointments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

export const getAdminApplications = (status = 'all') =>
  request(`/api/admin/applications?status=${encodeURIComponent(status)}`);

export const reviewApplication = (id, status, note) =>
  request(`/api/admin/applications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, note }),
  });

// -- Clinician portal -------------------------------------------------------
// Scoped server-side to the signed-in clinician's own therapist profile. Note
// there is no therapistId parameter to pass — that is deliberate.

export const getClinicianMe = () => request('/api/clinician/me');

export const getClinicianSummary = () => request('/api/clinician/summary');

export const getClinicianAppointments = (params = {}) => {
  const query = toQuery(params);
  return request(`/api/clinician/appointments${query ? `?${query}` : ''}`);
};

export const updateClinicianAppointmentStatus = (id, status) =>
  request(`/api/clinician/appointments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });

export { request };

export { BASE_URL };
